<template>
  <div v-hotkey="shortcuts" class="agenda" @contextmenu.prevent>
    <BaseTopBar v-if="isMounted" is-fit-content>
      <template #left>
        <div class="datepicker-container">
          <p ref="dateEl" class="date" @click="handleDateClick">
            {{ dateDisplay }}
          </p>
          <div class="nav">
            <BaseButton
              kind="auxiliary"
              type="secondary"
              :tooltip="{
                text: 'Previous day',
                shortcut: navigationActions.previousPeriod.shortcutsDisplay,
              }"
              tooltip-display="always"
              @click.stop="addDay(-1)"
            >
              <template #icon>
                <ChevronLeftIcon />
              </template>
            </BaseButton>
            <BaseButton
              kind="auxiliary"
              type="secondary"
              tooltip-display="always"
              :tooltip="{
                text: 'Next day',
                shortcut: navigationActions.nextPeriod.shortcutsDisplay,
              }"
              @click.stop="addDay(1)"
            >
              <template #icon>
                <ChevronRightIcon />
              </template>
            </BaseButton>
            <BaseButton
              v-if="!isToday"
              tooltip-display="always"
              :tooltip="{
                text: 'Today',
                shortcut: navigationActions.goToToday.shortcutsDisplay,
              }"
              kind="auxiliary"
              type="secondary"
              @click="goToday"
            >
              <template #icon>
                <BackIcon />
              </template>
            </BaseButton>
          </div>
        </div>
      </template>
    </BaseTopBar>
    <div class="sidebar" :class="{ isToday }">
      <Suspense>
        <Tasks
          ref="tasksEl"
          class="tasks"
          :date="date"
          @unselect-items="unselectItems"
        />
        <template #fallback>
          <Loader class="loader">
            <BaseSkeletonList />
          </Loader>
        </template>
      </Suspense>
    </div>
    <BaseIntegrationMissing
      v-if="hasCalendar !== 'synchronized'"
      class="no-default-calendar"
      feature="calendar"
      :synchronizing="hasCalendar === 'initializing'"
      empty-state-variant="calendar"
    />
    <BaseNoDefaultCalendar
      v-else-if="!defaultCalendar"
      class="no-default-calendar"
    />
    <div v-else class="wrapper-events">
      <Suspense>
        <Events
          ref="eventsEl"
          :date="date"
          :selected-calendar-id="selectedCalendarId"
          :calendars-map="calendarsMap"
          @unselect-items="unselectItems"
        />
        <template #fallback>
          <Loader><BaseTimelineSkeleton /></Loader>
        </template>
      </Suspense>
    </div>
    <DatePicker ref="datePickerRef" :modes="['date']" :show-clear="false" />
  </div>
</template>

<script lang="ts">
import {
  addDays,
  isToday as dateIsToday,
  format,
  getYear,
  startOfToday,
} from "date-fns";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import assert from "@/assert";
import { navigationActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseIntegrationMissing from "@/components/base/BaseIntegrationMissing.vue";
import BaseNoDefaultCalendar from "@/components/base/BaseNoDefaultCalendar.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import BaseTimelineSkeleton from "@/components/base/BaseTimelineSkeleton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import DatePicker from "@/components/DatePicker.vue";
import { getDateFromIso } from "@/components/dates";
import BackIcon from "@/components/icons/BackIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import {
  assignShortcuts,
  goToNextPeriod,
  goToNow,
  goToPreviousPeriod,
  Shortcuts,
} from "@/components/shortcuts";
import { useCalendarSwitcher } from "@/composables/useCalendarSwitcher";
import { useReleaser } from "@/ref";
import { navigateToAgenda } from "@/utils/navigate";

import { isDraggingTask } from "./dnd";
import Events from "./Events.vue";
import Tasks from "./Tasks.vue";

export default defineComponent({
  components: {
    BaseIntegrationMissing,
    BaseNoDefaultCalendar,
    BaseTimelineSkeleton,
    Loader,
    BaseSkeletonList,
    BackIcon,
    BaseButton,
    BaseTopBar,
    DatePicker,
    ChevronLeftIcon,
    ChevronRightIcon,
    Events,
    Tasks,
  },
  async setup() {
    const router = inject.router();
    const profile = inject.profile();
    const calendars = inject.calendars();
    const hasCalendar = inject.hasCalendar();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const clock = inject.clock();
    const route = useRoute();
    const isMounted = ref(false);
    const isTaskDragging = computed(isDraggingTask);

    onMounted(() => (isMounted.value = true));

    const date = computed<Date>(() =>
      route.query &&
      route.query["date"] &&
      typeof route.query["date"] === "string"
        ? getDateFromIso(route.query["date"])
        : startOfToday()
    );
    const currentDate = computed(() => clock.value.getDate());
    const day = computed(() => date.value.getDate());
    const isToday = computed(() => {
      //to be sure that isToday is refreshed when we change day.
      assert(currentDate.value);
      return dateIsToday(date.value);
    });
    const showYear = computed(() => {
      const selectedYear = getYear(date.value);
      const todayYear = getYear(startOfToday());
      return selectedYear !== todayYear;
    });
    const dateDisplay = computed(() => {
      if (showYear.value) {
        return format(date.value, "LLLL d, yyyy");
      }
      return `${format(date.value, "LLLL")} ${day.value}`;
    });
    const tasksEl = ref<InstanceType<typeof Tasks>>();
    const eventsEl = ref<InstanceType<typeof Events>>();
    const dateEl = ref<HTMLElement>();
    const datePickerRef = ref<InstanceType<typeof DatePicker>>();

    const unselectItems = () => {
      eventsEl.value?.unselectEvent();
      tasksEl.value?.unselectTask();
    };
    const addDay = (n: number) => {
      navigateToAgenda(router, addDays(date.value, n));
      unselectItems();
    };
    const goToDate = (date: Date) => {
      navigateToAgenda(router, date);
      unselectItems();
    };
    const handleDateClick = () => {
      const picker = datePickerRef.value;
      if (!picker || !dateEl.value) return;
      // Toggle: the picker's own click-outside handler ignores clicks on the
      // trigger, so a re-click lands here and closes it.
      if (picker.isDisplayed) {
        picker.closePicker();
        return;
      }
      picker.showPicker(dateEl.value, date.value, (selection) => {
        if (selection?.mode === "date") {
          goToDate(selection.date);
        }
      });
    };
    const goToday = () => goToDate(startOfToday());

    // For auto-focusing the "Add task..." input field
    const lastFocusAddTaskToken = ref<string | null>(null);
    watch(
      [tasksEl, () => route.query.focusAddTask],
      ([tasks, token]) => {
        const t = typeof token === "string" ? token : null;
        if (!tasks || !t || t === lastFocusAddTaskToken.value) return;
        lastFocusAddTaskToken.value = t;
        tasks.focusAddTask?.();
      },
      { flush: "post" }
    );
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      return {
        ...assignShortcuts(injection, goToNow, goToday),
        ...assignShortcuts(injection, goToNextPeriod, () => addDay(1)),
        ...assignShortcuts(injection, goToPreviousPeriod, () => addDay(-1)),
      };
    });
    watch(currentDate, () => {
      navigateToAgenda(router, startOfToday());
      unselectItems();
    });

    const releaser = useReleaser();
    const calendarSwitcherP = releaser.add(
      useCalendarSwitcher(calendars, profile)
    );
    const {
      defaultCalendar,
      calendarsMap,
      selectedCalendarId,
      registerKeyboardListener,
    } = await calendarSwitcherP;

    registerKeyboardListener(isTaskDragging);

    return {
      addDay,
      date,
      dateDisplay,
      day,
      dateEl,
      datePickerRef,
      defaultCalendar,
      hasCalendar,
      eventsEl,
      format,
      goToDate,
      goToday,
      handleDateClick,
      isMounted,
      isToday,
      navigationActions,
      shortcuts,
      tasksEl,
      unselectItems,
      selectedCalendarId,
      calendarsMap,
    };
  },
});
</script>

<style scoped lang="scss">
.datepicker-container {
  display: flex;
  flex-direction: row;
  column-gap: 4px;
  align-items: center;
  &:hover .nav {
    opacity: 1;
  }

  .date {
    @extend %font-body-regular;
    margin: 0;
    padding: 6px 8px;
    margin-inline-start: 4px;
    border-radius: $border-radius-m;
    @include transition(background-color 100ms ease-in);

    &:hover {
      @include themed() {
        background-color: t($bg-menu-hover);
      }
    }
  }

  .nav {
    display: flex;
    flex-direction: row;
    column-gap: 2px;
    @include transition(opacity 100ms ease-in);
  }
}

.agenda {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  overflow: clip;

  .wrapper-events {
    display: flex;
    width: 55%;
    justify-content: center;
    padding-top: 48px;
    height: 100%;
    box-sizing: border-box;
    overflow-y: auto;
  }

  .sidebar {
    display: flex;
    width: 45%;
    max-width: 650px;
    flex-direction: column;
    row-gap: 18px;
    overflow: hidden;
    white-space: nowrap;
    padding-top: 64px;

    .tasks {
      width: calc(100% - 32px);
      height: 100%;
      padding-inline-start: 32px;
      margin-inline-start: -16px;
      overflow: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .loader {
      margin-top: 40px;
      margin-inline-start: 32px;
    }
  }

  .no-default-calendar {
    width: 55%;
    margin-top: -64px;
  }
}
</style>
